import { useEffect, useMemo, useState } from 'react';

import { Grid } from '@mui/material';
import { toSentenceCase } from '@npi/buddy-utils';
import { useField } from 'formik';
import { Partner } from 'types/graphql';

import { useQuery } from '@redwoodjs/web';

import CountrySelectInput from 'src/components/CountrySelectInput/CountrySelectInput';
import { OptionType } from 'src/components/CustomInput';
import { DropDown } from 'src/components/FormikComponents';
import { Hierarchy } from 'src/constants';
import { useAddPartnerContext } from 'src/contexts/AddPartnerProvider';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { GET_PARTNERS } from 'src/graphql/queries/partners.query';
import { isIreland } from 'src/lib/countryConfig';
import { onError } from 'src/utils/utils';

const SelectPartnerType = () => {
    const { user } = useAuthContext();

    const [_, __, { setValue: updateTopLevelPartner }] = useField('topLevelPartner');
    const [{ value: selectedCountry }, ____, { setValue: updateCountry }] = useField('country');
    const [{ value: selectedPartnerType }, ___, { setValue: updatePartnerType }] =
        useField('partnerType');
    const { application } = useAddPartnerContext();
    const [partners, setPartners] = useState<Partner[]>([]);
    const [partnerType, setPartnerType] = useState<OptionType[]>([
        { value: Hierarchy.PARTNER_OF_PARTNER, label: 'Partner of Partner' },
    ]);
    const [isPartner, setIsPartner] = useState<boolean>(true);

    useQuery(GET_PARTNERS, {
        skip: isPartner,
        onError: (error) => {
            return onError(error);
        },
        onCompleted: ({ partners }) => {
            setPartners(partners);
        },
    });

    const partnerOptions = useMemo(() => {
        // Replace the existing logic with a simpler one
        const options = partners
            .filter((partner) => {
                return partner?.business?.legalName;
            })
            .map((partner) => {
                return {
                    value: partner.id.toString(),
                    label: toSentenceCase(partner.business.legalName.replace(/_/g, ' ')),
                };
            });

        return options;
    }, [partners]);

    // If the user is an admin, include the option to add a 'Partner' type and clear the initial form value for this field
    useEffect(() => {
        // User is an admin, only partners and admins can view this page
        if (
            user &&
            (user.role.hierarchyLevel === Hierarchy.NPI ||
                user.role.hierarchyLevel === Hierarchy.PARTNER)
        ) {
            setPartnerType([
                { value: Hierarchy.PARTNER, label: 'Partner' },
                { value: Hierarchy.PARTNER_OF_PARTNER, label: 'Partner of Partner' },
            ]);
            setIsPartner(false);
        }
    }, [user, application]);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <DropDown
                    name="partnerType"
                    label="Select a Partner Type"
                    options={partnerType}
                    placeholder="Select partner type"
                />
            </Grid>

            {selectedPartnerType === Hierarchy.PARTNER_OF_PARTNER && (
                <Grid item xs={12} sm={6}>
                    <DropDown
                        name="topLevelPartner"
                        label="Select Partner"
                        options={partnerOptions}
                        placeholder="Select partner"
                    />
                </Grid>
            )}

            <Grid
                item
                xs={12}
                sm={6}
                smOffset={selectedPartnerType === Hierarchy.PARTNER_OF_PARTNER ? 0 : 6}
            >
                <CountrySelectInput
                    setFieldValue={(__, value) => {
                        return updateCountry(value);
                    }}
                    filterByAlpha2Codes={isIreland ? ['IE'] : ['GB', 'XI']}
                    fieldName="country"
                    label="Select Country of Partner"
                    placeholder="Select Country of Partner"
                />
            </Grid>
        </Grid>
    );
};
export default SelectPartnerType;
